import { repeat } from "@@/utilities/repeat";
import type { Except } from "type-fest";
import { z } from "zod";

import { acquireType } from "../../acquire/acquirable";
import {
    accommodationSettingsZodSchema,
    bookableSettingsZodSchema,
} from "../../acquire/bookable-settings";
import { bookableTypes } from "../../acquire/bookable-type";

import { bookableOptionPricePerCalculator } from "../../acquire/resource-reference-options/bookable-option-price-calculator";
import { type QuantityPickerBookableOptionValue } from "../../acquire/resource-reference-options/quantity-picker-bookable-option-value";
import { resourceReferenceIdZodSchema } from "../../acquire/resource-references/resource-reference-id";

import { productIdZodSchema } from "../../products/product-id";
import { skuIdZodSchema } from "../../products/sku-id";
import { providerIdZodSchema } from "../../providers/provider-id";
import { ticketIdZodSchema } from "../../tickets/ticket-id";

import { accommodationQuantityZodObject } from "@@/shared/acquire/accommodation-quantity";
import { accommodationRateIdZodSchema } from "@@/shared/acquire/accommodation-rate-id";
import { fromExtrasZodSchema } from "@@/shared/acquire/extras-group";
import { bookableOptionIdZodSchema } from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import { bookableOptionValueIdZodSchema } from "@@/shared/acquire/resource-reference-options/bookable-option-value-id";
import { pickedPickedAccommodationTypeZodObject } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { resourceIdZodSchema } from "@@/shared/acquire/resources/resource-id";
import { weekAvailabilityZodSchema } from "@@/shared/date-time/availability/week-availability";
import { now } from "@@/shared/date-time/date-time";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import {
    isTimeRangeOverDST,
    timeRangeZodSchema,
    type TimeRange,
} from "@@/shared/date-time/time-range";
import { inMinutes } from "@@/shared/date-time/time-spans";
import { discountCodeIdZodSchema } from "@@/shared/discounts/codes/discount-code-id";
import { discountZodSchema, type Discount } from "@@/shared/discounts/discount";
import { geoLocationZodSchema } from "@@/shared/locations/geo-location";
import {
    metaContentFactory,
    metaContentZodSchema,
} from "@@/shared/meta-content/meta-content";
import {
    optionalUnionZodSchema,
    type PickedOptional,
} from "@@/shared/optionals/picked-optional";
import {
    priceFactory,
    priceZodSchema,
    type Price,
} from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { quoteItemIdZodSchema } from "@@/shared/quotes/quote-item-id";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { amountZodSchema } from "@@/shared/units-and-quantities/amount";
import {
    quantityZodSchema,
    type Quantity,
} from "@@/shared/units-and-quantities/quantity";
import {
    orderItemIdFactory_V2,
    orderItemIdZodSchema_V2,
    type OrderItemId,
} from "./order-item-id";

const _orderItemBaseZodSchema_V2 = z.object({
    _id: orderItemIdZodSchema_V2,
    _type: z.literal("ORDER_ITEM_V2"),
    _createdAt: isoAndUnixZodSchema,
    meta: metaContentZodSchema,
    providerId: providerIdZodSchema,
    location: geoLocationZodSchema.optional(),

    messageToProvider: z.string().nullable(),
    discounts: z.array(discountZodSchema),
    price: priceZodSchema,
    pricePerItem: priceZodSchema,
    originalPrice: priceZodSchema.optional(),
    originalPricePerItem: priceZodSchema.optional(),
    articleNumber: z.string().nullish(),
    availability: weekAvailabilityZodSchema.optional(),
    trackStockBalance: z.boolean().optional(),
    parentId: orderItemIdZodSchema_V2.optional(),
    extraGroup: fromExtrasZodSchema.optional(),
});

type _OrderItemBase_V2 = z.infer<typeof _orderItemBaseZodSchema_V2>;
const orderItemBuyableZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal(acquireType.BUYABLE),
        productId: productIdZodSchema,
        skuId: skuIdZodSchema,
        quantity: quantityZodSchema,
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        // All new buyable order items has this property
        amount: amountZodSchema.optional(),
        settings: z
            .object({
                orderItemStatusEnabled: z.boolean().optional(),
            })
            .optional(),
    }),
);
type OrderItemBuyable_V2 = z.infer<typeof orderItemBuyableZodSchema_V2>;

const orderItemOptionalZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("OPTIONAL"),
        parentId: orderItemIdZodSchema_V2,
        optional: optionalUnionZodSchema,
        quantity: quantityZodSchema,
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        settings: z.never().or(z.undefined()),
    }),
);
type OrderItemOptional_V2 = z.infer<typeof orderItemOptionalZodSchema_V2>;

const orderItemBookableZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal(acquireType.BOOKABLE),
        productId: productIdZodSchema,
        skuId: skuIdZodSchema,
        time: timeRangeZodSchema,
        quantity: quantityZodSchema,
        bookingType: z.enum(bookableTypes),
        settings: bookableSettingsZodSchema,
        hasAccess: z.boolean().optional(),
    }),
);
type OrderItemBookable_V2 = z.infer<typeof orderItemBookableZodSchema_V2>;

const orderItemBookableQuantityZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("RESOURCE_REFERENCE"),
        time: timeRangeZodSchema,
        parentId: orderItemIdZodSchema_V2,
        bookingType: z.enum(bookableTypes),
        quantity: quantityZodSchema,
        reference: resourceReferenceIdZodSchema,
        optionId: bookableOptionIdZodSchema.optional(),
        optionValueId: bookableOptionValueIdZodSchema.optional(),
        main: z.boolean(),
    }),
);
type OrderItemBookableQuantity_V2 = z.infer<
    typeof orderItemBookableQuantityZodSchema_V2
>;

const orderItemAccommodationZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal(acquireType.ACCOMMODATION),
        productId: productIdZodSchema,
        skuId: skuIdZodSchema,
        time: timeRangeZodSchema,
        //
        //Legacy try to remove????
        quantity: quantityZodSchema,
        quantities: z.array(
            z.object({
                type: accommodationQuantityZodObject,
                quantity: quantityZodSchema,
            }),
        ),

        settings: accommodationSettingsZodSchema,
        //The picked resource/room group more data?
        accommodation: z.object({
            title: translatableZodSchema, //ResourceGroup title/name
            resourceId: resourceIdZodSchema, //GroupId should it contain more data?
            rateId: accommodationRateIdZodSchema, //Picked rates

            type: pickedPickedAccommodationTypeZodObject, //Room type
        }),
        hasAccess: z.boolean().optional(),
    }),
);

type OrderItemAccommodation_V2 = z.infer<
    typeof orderItemAccommodationZodSchema_V2
>;

const orderItemAccommodationRateItemZodSchema_V2 =
    _orderItemBaseZodSchema_V2.merge(
        z.object({
            acquireType: z.literal("ACCOMMODATION_RATE_ITEM"),
            parentId: orderItemIdZodSchema_V2,
            productId: productIdZodSchema,
            skuId: skuIdZodSchema,
            quantity: quantityZodSchema,
            time: timeRangeZodSchema,
            rateId: accommodationRateIdZodSchema,
        }),
    );

type OrderItemAccommodationRateItem_V2 = z.infer<
    typeof orderItemAccommodationRateItemZodSchema_V2
>;

const orderItemVoucherZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal(acquireType.VOUCHER),
        productId: productIdZodSchema,
        skuId: skuIdZodSchema,
        quantity: quantityZodSchema,
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        settings: z.never().or(z.undefined()),
        /**
         * All new voucher order items has this property
         * @type {Amount}
         */
        amount: amountZodSchema.optional(),
        hasTicket: z.boolean().optional(),
    }),
);
type OrderItemVoucher_V2 = z.infer<typeof orderItemVoucherZodSchema_V2>;

const orderItemGiftCardZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal(acquireType.GIFTCARD),
        productId: productIdZodSchema,
        skuId: skuIdZodSchema,
        quantity: quantityZodSchema,
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        settings: z.never().or(z.undefined()),
        /**
         * All new gift card order items has this property
         * @type {Amount}
         */
        amount: amountZodSchema.optional(),
    }),
);
type OrderItemGiftCard_V2 = z.infer<typeof orderItemGiftCardZodSchema_V2>;

const orderItemUsedGiftCardZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("GIFTCARD_USED"),
        ticketId: ticketIdZodSchema,
        code: z.string(),
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        quantity: quantityZodSchema,
        settings: z.never().or(z.undefined()),
        /**
         * All new voucher order items has this property
         * @type {Amount}
         */
        amount: amountZodSchema.optional(),
    }),
);
type OrderItemUsedGiftCard_V2 = z.infer<
    typeof orderItemUsedGiftCardZodSchema_V2
>;

/**
 * !Denna är bara till för rabattkoder, inte för andra rabatter
 * !Discount Codes, inte discounts generellt.
 */
const orderItemDiscountZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("DISCOUNT"),
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        settings: z.never().or(z.undefined()),
        code: z.string(),
        quantity: quantityZodSchema,
        discountCodeId: discountCodeIdZodSchema,
        type: z.union([z.literal("VALUE"), z.literal("PERCENTAGE")]),
        value: z.number(),
    }),
);

type OrderItemDiscount_V2 = z.infer<typeof orderItemDiscountZodSchema_V2>;

const orderItemGenericDiscountZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("GENERIC_DISCOUNT"),
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        settings: z.never().or(z.undefined()),
        quantity: quantityZodSchema,
        type: z.union([z.literal("VALUE"), z.literal("PERCENTAGE")]),
        value: z.number(),
    }),
);

type OrderItemGenericDiscount_V2 = z.infer<
    typeof orderItemGenericDiscountZodSchema_V2
>;

const orderItemGenericZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("GENERIC"),
        time: z.never().or(z.undefined()),
        reservations: z.never().or(z.undefined()),
        settings: z.never().or(z.undefined()),
        quantity: quantityZodSchema,
        parentId: orderItemIdZodSchema_V2.optional(),
    }),
);

// Define the OrderItemQuoteHead schema
const orderItemQuoteHeadZodSchema_V2 = _orderItemBaseZodSchema_V2.merge(
    z.object({
        acquireType: z.literal("QUOTE_HEAD"),
        time: timeRangeZodSchema,
        quoteItemId: quoteItemIdZodSchema,
        quantity: quantityZodSchema,
    }),
);

// Define the TypeScript type for OrderItemQuoteHead
type OrderItemQuoteHead_V2 = z.infer<typeof orderItemQuoteHeadZodSchema_V2>;

// Factory function parameters type
type OrderItemQuoteHeadFactoryParams_V2 = Except<
    OrderItemQuoteHead_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };

// Factory function to create OrderItemQuoteHead instances
const orderItemQuoteHeadFactory_V2 = (
    params: OrderItemQuoteHeadFactoryParams_V2,
): OrderItemQuoteHead_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: nowIsoAndUnix(),
        _type: "ORDER_ITEM_V2",
        acquireType: "QUOTE_HEAD",
        ...params,
    };
};

type OrderItemGeneric_V2 = z.infer<typeof orderItemGenericZodSchema_V2>;

type OrderItem_V2 =
    | OrderItemBookableQuantity_V2
    | OrderItemBookable_V2
    | OrderItemBuyable_V2
    | OrderItemDiscount_V2
    | OrderItemGenericDiscount_V2
    | OrderItemGiftCard_V2
    | OrderItemOptional_V2
    | OrderItemUsedGiftCard_V2
    | OrderItemVoucher_V2
    | OrderItemAccommodation_V2
    | OrderItemAccommodationRateItem_V2
    | OrderItemGeneric_V2
    | OrderItemQuoteHead_V2;

const orderItemZodSchema_V2: z.ZodSchema<OrderItem_V2> = z.discriminatedUnion(
    "acquireType",
    [
        orderItemBookableQuantityZodSchema_V2,
        orderItemBookableZodSchema_V2,
        orderItemBuyableZodSchema_V2,
        orderItemDiscountZodSchema_V2,
        orderItemGenericDiscountZodSchema_V2,
        orderItemGiftCardZodSchema_V2,
        orderItemOptionalZodSchema_V2,
        orderItemUsedGiftCardZodSchema_V2,
        orderItemVoucherZodSchema_V2,
        orderItemAccommodationZodSchema_V2,
        orderItemAccommodationRateItemZodSchema_V2,
        orderItemGenericZodSchema_V2,
        orderItemQuoteHeadZodSchema_V2,
    ],
);

type OrderItemWithProduct_V2 =
    | OrderItemBookable_V2
    | OrderItemBuyable_V2
    | OrderItemGiftCard_V2
    | OrderItemVoucher_V2
    | OrderItemAccommodation_V2;

const productOrderItemZodSchema_V2: z.ZodSchema<OrderItemWithProduct_V2> =
    z.discriminatedUnion("acquireType", [
        orderItemBookableZodSchema_V2,
        orderItemBuyableZodSchema_V2,
        orderItemGiftCardZodSchema_V2,
        orderItemVoucherZodSchema_V2,
        orderItemAccommodationZodSchema_V2,
    ]);

type OrderItemBuyableFactoryParams_V2 = Except<
    OrderItemBuyable_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemBuyableFactory_V2 = (
    params: OrderItemBuyableFactoryParams_V2,
): OrderItemBuyable_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: nowIsoAndUnix(),
        _type: "ORDER_ITEM_V2",
        acquireType: "BUYABLE",
        ...params,
    };
};

type OrderItemBookableFactoryParams_V2 = Except<
    OrderItemBookable_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemBookableFactory_V2 = (
    params: OrderItemBookableFactoryParams_V2,
): OrderItemBookable_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "BOOKABLE",
        ...params,
    };
};

type OrderItemAccommodationFactoryParams_V2 = Except<
    OrderItemAccommodation_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemAccommodationFactory_V2 = (
    params: OrderItemAccommodationFactoryParams_V2,
): OrderItemAccommodation_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "ACCOMMODATION",
        ...params,
    };
};

type OrderItemAccommodationRateFactoryParams_V2 = Except<
    OrderItemAccommodationRateItem_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemAccommodationRateFactory_V2 = (
    params: OrderItemAccommodationRateFactoryParams_V2,
): OrderItemAccommodationRateItem_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "ACCOMMODATION_RATE_ITEM",
        ...params,
    };
};

type OrderItemVoucherFactoryParams_V2 = Except<
    OrderItemVoucher_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemVoucherFactory_V2 = (
    params: OrderItemVoucherFactoryParams_V2,
): OrderItemVoucher_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "VOUCHER",
        ...params,
    };
};

type OrderItemGiftCardFactoryParams_V2 = Except<
    OrderItemGiftCard_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemGiftCardFactory_V2 = (
    params: OrderItemGiftCardFactoryParams_V2,
): OrderItemGiftCard_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "GIFTCARD",
        ...params,
    };
};

type OrderItemUsedGiftCardFactoryParams_V2 = Except<
    OrderItemUsedGiftCard_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemUsedGiftCardFactory_V2 = (
    params: OrderItemUsedGiftCardFactoryParams_V2,
): OrderItemUsedGiftCard_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "GIFTCARD_USED",
        ...params,
    };
};

/**
 * A `DISCOUNT` order item is an order item
 * representing a discount code
 */
type OrderItemDiscountFactoryParams_V2 = Except<
    OrderItemDiscount_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemDiscountFactory_V2 = (
    params: OrderItemDiscountFactoryParams_V2,
): OrderItemDiscount_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "DISCOUNT",
        ...params,
    };
};

/**
 * A `GENERIC_DISCOUNT` order item is a discount manually
 * created by a user e.g. during an order change
 */
type OrderItemGenericDiscountFactoryParams_V2 = Except<
    OrderItemGenericDiscount_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemGenericDiscountFactory_V2 = (
    params: OrderItemGenericDiscountFactoryParams_V2,
): OrderItemGenericDiscount_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "GENERIC_DISCOUNT",
        ...params,
    };
};

/**
 *
 * @param params
 *
 * @returns
 */
type OrderItemGenericFactoryParams_V2 = Except<
    OrderItemGeneric_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemGenericFactory_V2 = (
    params: OrderItemGenericFactoryParams_V2,
): OrderItemGeneric_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "GENERIC",
        ...params,
    };
};

type OrderItemBookableQuantityFactoryParams_V2 = Except<
    OrderItemBookableQuantity_V2,
    "_id" | "_createdAt" | "_type" | "acquireType"
> & { _id?: OrderItemId };
const orderItemBookableQuantityFactory_V2 = (
    params: OrderItemBookableQuantityFactoryParams_V2,
): OrderItemBookableQuantity_V2 => {
    return {
        _id: params._id ?? orderItemIdFactory_V2(),
        _createdAt: now(),
        _type: "ORDER_ITEM_V2",
        acquireType: "RESOURCE_REFERENCE",
        ...params,
    };
};

const orderItemsOptional = (params: {
    // sku: Sku;
    // product: Product;
    // provider: Provider;
    // discounts: Discount[];
    quantity: Quantity;
    pickedOptions: PickedOptional[];
    parent: OrderItem_V2;
    totalPrice: Price;
    pricePerItem: Price;
}): OrderItem_V2[] => {
    // Parse all prices from sku and picked options
    // their combined sum will be the price of the cart item
    const { quantity, pickedOptions, totalPrice, parent, pricePerItem } =
        params;

    const parentTotalPrice = totalPrice;

    const pickedOptionals = pickedOptions.map(pickedOption =>
        orderItemOptionalFactory_V2({
            pickedOption,
            discounts: [],
            messageToProvider: undefined,
            productPricePerItem: pricePerItem,
            productPrice: parentTotalPrice,
            quantity,
            parent: parent,
        }),
    );

    return [...pickedOptionals];
};

const orderItemOptionalFactory_V2 = (params: {
    parent: OrderItem_V2;

    pickedOption: PickedOptional;
    discounts: Discount[];

    messageToProvider: string | undefined;
    productPricePerItem: Price;
    productPrice: Price;
    quantity: Quantity;
}) => {
    const {
        pickedOption,
        discounts,
        messageToProvider,
        // options,
        productPrice,
        quantity,
        parent,
    } = params;

    /**
     * create a unique but reproducable id
     * for every unique cart item.
     * uniqueness based on sku id and picked option ids
     */

    const orderItemV2Id = orderItemIdFactory_V2();

    const optionPricePer =
        pickedOption.optional._type === "OPTIONAL_PERCENTAGE"
            ? priceFactory({
                  amountIncludingVat:
                      productPrice.amountIncludingVat *
                      pickedOption.optional.percentage,
                  vat: productPrice.vat,
              })
            : pickedOption.optional.price;

    const totalPrice = sumPrices(repeat(optionPricePer, quantity.value));

    const orderItem: OrderItemOptional_V2 = {
        _id: orderItemV2Id,
        _type: "ORDER_ITEM_V2",
        _createdAt: now(),
        acquireType: "OPTIONAL",
        parentId: parent._id,
        discounts: discounts,
        providerId: parent.providerId,
        articleNumber: undefined,
        messageToProvider: messageToProvider || null,

        meta: metaContentFactory({
            title: pickedOption.optional.meta.title,
            kicker: pickedOption.optional.meta.kicker,
            subtitle: pickedOption.optional.meta.subtitle,
            description: pickedOption.optional.meta.description,
        }),
        optional: pickedOption.optional,
        pricePerItem: optionPricePer, // pricePerItem,
        price: totalPrice, //price,
        originalPricePerItem: optionPricePer, //pricePerItem,
        originalPrice: totalPrice, //price,
        quantity,
        availability: undefined,
        trackStockBalance: false,
    };
    return orderItem;
};

const orderItemReservationFactory_V2 = (params: {
    parent: OrderItemBookable_V2;

    value: QuantityPickerBookableOptionValue;
    discounts: Discount[];

    messageToProvider: string | undefined;

    quantity: Quantity;
    time: TimeRange;
    main: boolean;
}) => {
    const { value, discounts, messageToProvider, parent, time, main } = params;

    //const orderItemV2Id = orderItemIdFactory_V2();

    const numberOfBookedTime = inMinutes({ seconds: time.duration });

    const perPrice = bookableOptionPricePerCalculator({
        bookedMinutes: numberOfBookedTime,
        option: value,
        isSpanningDST: isTimeRangeOverDST(time),
    });

    const totalPrice = sumPrices(repeat(perPrice, value.quantity.value));

    const orderItem = orderItemBookableQuantityFactory_V2({
        parentId: parent._id,
        discounts: discounts,
        providerId: parent.providerId,
        articleNumber: undefined,
        messageToProvider: messageToProvider || null,
        bookingType: parent.bookingType ?? "GENERIC",
        meta: metaContentFactory({
            title: value.meta.title,
            kicker: value.meta.kicker,
            subtitle: value.meta.title,
            description: value.meta.description,
        }),

        pricePerItem: perPrice, // pricePerItem,
        price: totalPrice, //price,
        originalPricePerItem: perPrice, //pricePerItem,
        originalPrice: totalPrice, //price,
        quantity: value.quantity,
        availability: undefined,
        trackStockBalance: false,
        reference: value.resourceReferenceId,
        optionId: value.parentId,
        optionValueId: value._id,
        time: time,
        main: main,
    });

    // const orderItem2: OrderItemBookableQuantity_V2 = {
    //     _id: orderItemV2Id,
    //     _type: "ORDER_ITEM_V2",
    //     _createdAt: now(),
    //     acquireType: "RESOURCE_REFERENCE",
    //     parentId: parent._id,
    //     discounts: discounts,
    //     providerId: parent.providerId,
    //     articleNumber: undefined,
    //     messageToProvider: messageToProvider || null,
    //     bookingType: parent.bookingType ?? "GENERIC",
    //     meta: metaContentFactory({
    //         title: value.meta.title,
    //         kicker: value.meta.kicker,
    //         subtitle: value.meta.title,
    //         description: value.meta.description,
    //     }),

    //     pricePerItem: perPrice, // pricePerItem,
    //     price: totalPrice, //price,
    //     originalPricePerItem: perPrice, //pricePerItem,
    //     originalPrice: totalPrice, //price,
    //     quantity: value.quantity,
    //     availability: undefined,
    //     trackStockBalance: false,
    //     reference: value.resourceReferenceId,
    //     optionId: value.parentId,
    //     optionValueId: value._id,
    //     time: time,
    //     main: main,
    // };
    return orderItem;
};

export {
    orderItemAccommodationFactory_V2,
    orderItemAccommodationRateFactory_V2,
    orderItemBookableFactory_V2,
    orderItemBookableQuantityFactory_V2,
    orderItemBookableZodSchema_V2,
    orderItemBuyableFactory_V2,
    orderItemBuyableZodSchema_V2,
    orderItemDiscountFactory_V2,
    orderItemGenericDiscountFactory_V2,
    orderItemGenericFactory_V2,
    orderItemGiftCardFactory_V2,
    orderItemGiftCardZodSchema_V2,
    orderItemOptionalFactory_V2,
    orderItemOptionalZodSchema_V2,
    orderItemQuoteHeadFactory_V2,
    orderItemReservationFactory_V2,
    orderItemsOptional,
    orderItemUsedGiftCardFactory_V2,
    orderItemVoucherFactory_V2,
    orderItemVoucherZodSchema_V2,
    orderItemZodSchema_V2,
    productOrderItemZodSchema_V2,
};
export type {
    OrderItem_V2,
    OrderItemAccommodation_V2,
    OrderItemAccommodationRateItem_V2,
    OrderItemBookable_V2,
    OrderItemBookableQuantity_V2,
    OrderItemBuyable_V2,
    OrderItemDiscount_V2,
    OrderItemGeneric_V2,
    OrderItemGenericDiscount_V2,
    OrderItemGiftCard_V2,
    OrderItemOptional_V2,
    OrderItemQuoteHead_V2,
    OrderItemUsedGiftCard_V2,
    OrderItemVoucher_V2,
    OrderItemWithProduct_V2,
};
